import React, { useState, useEffect } from "react";
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faFileExcel, faPrint } from '@fortawesome/free-solid-svg-icons';
import './SaleOrder.css';

import useTableSelection from './shared/useTableSelection';
import TableToolbar from './shared/TableToolbar';
import { exportToPrint, exportToExcel, exportToPDF } from './shared/exportTable';

const AllSaleOrder = ({ openEditDrawer, refreshTrigger }) => {
    const [ordersData, setOrdersData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    const userType = localStorage.getItem("usertype");
    const loggedInUserId = localStorage.getItem("id") || localStorage.getItem("user_id");

    const fetchOrders = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get('https://skcapi.btcjapan.co/get_sale_orders.php', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.data.success) {
                let fetchedOrders = res.data.data;

                if (userType === 'administrator') {
                    // Admin sees all
                } else if (userType === 'leader') {
                    let leadersGroupId = null;
                    for (const order of fetchedOrders) {
                        if (order.assigned_users && Array.isArray(order.assigned_users)) {
                            const found = order.assigned_users.find(u => String(u.id) === String(loggedInUserId));
                            if (found && order.group_id) {
                                leadersGroupId = order.group_id;
                                break;
                            }
                        }
                    }
                    if (leadersGroupId) {
                        fetchedOrders = fetchedOrders.filter(order => order.group_id === leadersGroupId);
                    } else {
                        fetchedOrders = [];
                    }
                } else if (userType === 'member') {
                    fetchedOrders = fetchedOrders.filter(order => 
                        order.assigned_users && Array.isArray(order.assigned_users) &&
                        order.assigned_users.some(u => String(u.id) === String(loggedInUserId))
                    );
                }

                setOrdersData(fetchedOrders);
            } else {
                setOrdersData([]);
            }
        } catch (err) { 
            console.error("Fetch Error:", err); 
            setOrdersData([]); 
        } finally { 
            setLoading(false); 
        }
    };

    useEffect(() => {
        fetchOrders();
    }, [refreshTrigger]);

    const handleDelete = async (id) => {
        if (userType !== 'administrator') {
            alert("Only Super Admin can delete Sale Orders.");
            return;
        }
        if (!window.confirm("Are you sure you want to delete this Sale Order?")) return;
        try {
            const token = localStorage.getItem("token");
            await axios.post('https://skcapi.btcjapan.co/delete_sale_order.php', { id }, { headers: { Authorization: `Bearer ${token}` } });
            fetchOrders();
        } catch (err) {
            alert("Error deleting Sale Order");
            console.error(err);
        }
    };

    const filteredOrders = ordersData.filter(order => {
        const term = searchTerm.toLowerCase();
        return (
            (order.ref_no || "").toLowerCase().includes(term) ||
            (order.client_name || "").toLowerCase().includes(term) ||
            (order.status || "").toLowerCase().includes(term) ||
            (order.country || "").toLowerCase().includes(term)
        );
    });

    // ===== SELECTION =====
    const selection = useTableSelection(filteredOrders, (row) => row.id);

    // Format assigned-to into a readable string for export
    const formatAssignedTo = (order) => {
        if (order.assigned_to_type === 'everyone') return 'Everyone';
        if (!Array.isArray(order.assigned_users) || order.assigned_users.length === 0) return '';
        return order.assigned_users.map(u => u.name).join(', ');
    };

    // ===== EXPORT CONFIG =====
    const exportColumns = [
        'Client',
        'Chassis Number',
        'Date',
        'Expired',
        'Status',
        'Country',
        'Currency',
        'Total',
        'Assigned To',
    ];

    const buildExportRows = (rows) => rows.map(order => [
        order.client_name || '',
        order.chassis_number || '',
        order.sale_order_date || '',
        order.expired_date || '',
        order.status || 'Draft',
        order.country || '',
        order.currency || 'USD',
        (Number(order.total) || 0).toFixed(2),
        formatAssignedTo(order),
    ]);

    const handleExport = (kind) => {
        const rows = selection.selectedRows;
        if (rows.length === 0) return;
        const title = 'Sale Orders Export';
        const data = buildExportRows(rows);
        if (kind === 'print') exportToPrint(title, exportColumns, data);
        if (kind === 'excel') exportToExcel(title, exportColumns, data);
        if (kind === 'pdf')   exportToPDF(title, exportColumns, data);
    };

    return (
        <div className="sale-order-list-container">
            <TableToolbar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                isSelectMode={selection.isSelectMode}
                onToggleSelectMode={selection.toggleSelectMode}
                onSelectAll={() =>
                    selection.allSelected ? selection.clearSelection() : selection.selectAll()
                }
                selectedCount={selection.selectedIds.size}
                allSelected={selection.allSelected}
                onPrint={() => handleExport('print')}
                onExcel={() => handleExport('excel')}
                onPDF={() => handleExport('pdf')}
            />            

            <div className="table-responsive">
                <table className="sale-order-table">
                    <thead>
                        <tr>
                            {selection.isSelectMode && (
                                <th style={{ width: 42, textAlign: 'center' }}>
                                    <input
                                        type="checkbox"
                                        checked={selection.allSelected}
                                        onChange={() =>
                                            selection.allSelected
                                                ? selection.clearSelection()
                                                : selection.selectAll()
                                        }
                                        title={selection.allSelected ? 'Unselect All' : 'Select All'}
                                    />
                                </th>
                            )}
                            <th>Client</th>
                            <th>Chassis Number</th>
                            <th>Date</th>
                            <th>Expired</th>
                            <th>Status</th>
                            <th>Country</th>
                            <th>Currency</th>
                            <th>Total</th>
                            <th>Assigned To</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="10" style={{textAlign:"center", padding: 40, color:"#888"}}>Loading Sale Orders...</td></tr>
                        ) : filteredOrders.length === 0 ? (
                            <tr><td colSpan="10" style={{textAlign:"center", padding: 40, color:"#888"}}>No Sale Orders found.</td></tr>
                        ) : (
                            filteredOrders.map(order => (
                                <tr key={order.id}> 
                                    {selection.isSelectMode && (
                                        <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={selection.isRowSelected(order.id)}
                                                onChange={() => selection.toggleRow(order.id)}
                                            />
                                        </td>
                                    )}                                   
                                    <td>{order.client_name || 'Select Client'}</td>
                                    <td>{order.chassis_number || 'Select Client'}</td>
                                    <td>{order.sale_order_date || 'N/A'}</td>
                                    <td>{order.expired_date || 'N/A'}</td>
                                    <td>
                                        <span className={`status-badge ${order.status === 'Accepted' ? 'green' : 'orange'}`}>{order.status || 'Draft'}</span>
                                    </td>
                                    <td>{order.country || 'N/A'}</td>
                                    <td>{order.currency || 'USD'}</td>
                                    <td>{(Number(order.total) || 0).toFixed(2)}</td>
                                    <td>
                                        <div className="avatar-group">
                                            {order.assigned_to_type === 'everyone' ? (
                                                <span style={{background: '#6f42c1', color: '#fff', padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500}}>Everyone</span>
                                            ) : (
                                                order.assigned_users?.slice(0, 3).map((user, idx) => (
                                                    <div key={idx} style={{width: 30, height: 30, borderRadius: '50%', background: user.image ? 'transparent' : '#007bff', overflow: 'hidden', border: '2px solid #fff', marginLeft: '-5px'}}>
                                                        {user.image ? <img src={user.image} style={{width:'100%', height:'100%', objectFit:'cover'}} /> : <div style={{width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:'12px'}}>{user.name?.charAt(0)}</div>}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="action-btns">
                                            {/* 
                                            <button className="action-icon-btn edit" onClick={() => openEditDrawer(order.id)}>✎</button>
                                            */}
                                            <button className="action-icon-btn edit" onClick={() => openEditDrawer(order.id, 'edit')}>✎</button>
                                            <button className="action-icon-btn delete" onClick={() => handleDelete(order.id)} disabled={userType !== 'administrator'}>🗑</button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AllSaleOrder;